import axios, { HttpStatusCode } from "axios";
import { ApiRoutes } from "../routes/apiRoutes";
import { navigateTo } from "../_constants/navigation";
import { CryptoService } from "../utils/cryptoService";

const noAuthEndpoints = [ApiRoutes.AUTH.LOGIN, ApiRoutes.AUTH.REFRESH];

const noEncryptionEndpoints = [
  { urlPrefix: ApiRoutes.ESL.StoreProductData, methods: ['PUT'] },
  { urlPrefix: "/esl/lcd/import-binding", methods: ['POST'] },
  // { urlPrefix: "/esl/lcd/preview", methods: ['POST'] },
];

function isNoEncryption(config) {
  const url = config.url || '';
  const method = (config.method || '').toLowerCase();
  const matchedRule = noEncryptionEndpoints.find(
    (rule) =>
      url.startsWith(rule.urlPrefix) &&
      (!rule.methods || rule.methods.some((m) => m.toLowerCase() === method)),
  );
  return !!matchedRule;
}
const ID_PATTERNS = [
  /^[a-f0-9]{24}$/i, // MongoDB ObjectId
  /^[A-F0-9]{10,32}$/i, // MAC (10-32 hex chars)
  /^([0-9A-F]{2}[:-]){5}([0-9A-F]{2})$/i, // MAC with colons/dashes
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, // UUID
];

function isEncryptableId(segment) {
  return ID_PATTERNS.some((pattern) => pattern.test(segment));
}

export const globalAxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 60000 * 2,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json; charset=utf-8",
    "accept-language": "en",
  },
  withCredentials: true,
  method: "GET",
});

function encryptBody(formData) {
  return CryptoService.encrypt(JSON.stringify(formData));
}

export function decryptBody(apiResponse) {
  return CryptoService.decrypt(apiResponse);
}

function extractBaseRoutes(obj, prefix = "") {
  let routes = [];
  for (const key in obj) {
    if (typeof obj[key] === "string") {
      routes.push(obj[key]);
    } else if (typeof obj[key] === "object") {
      routes = routes.concat(extractBaseRoutes(obj[key], prefix));
    }
  }
  return routes;
}

const baseRoutes = extractBaseRoutes(ApiRoutes);

globalAxiosInstance.interceptors.request.use(
  (config) => {
    config.method = config.method ?? "GET";
    config.headers = { ...config.headers };
    config.params = { ...config.params, flag: "GUI" };

    const originalUrl = config.url || "";

    const isNoAuth = noAuthEndpoints.some((route) =>
      originalUrl.startsWith(route),
    );

    if (config.url) {
      const match = baseRoutes
        .filter((route) => config.url.startsWith(route))
        .sort((a, b) => b.length - a.length)[0];

      if (match) {
        const parts = config.url.split("/").filter(Boolean);
        const matchParts = match.split("/").filter(Boolean);
        const baseLength = matchParts.length;

        const encryptedParts = parts.map((segment, index) => {
          if (index < baseLength) {
            return segment;
          }
          if (isEncryptableId(segment)) {
            return CryptoService.encrypt(segment);
          }
          return segment;
        });

        config.url = "/" + encryptedParts.join("/");
      }
    }

    if (!isNoAuth) {
      const authToken = localStorage.getItem("token");

      if (!authToken) {
        navigateTo("/");
        throw new Error("No auth token found — request aborted.");
      }

      config.headers.Authorization = `Bearer ${authToken}`;
    }

    if (isNoEncryption(config)) {
      config.headers = { ...config.headers };
      if (config.data instanceof FormData) {
        delete config.headers["Content-Type"];
      }
      if (config.params) {
        delete config.params.flag;
      }
    }

    if (!isNoEncryption(config)) {
      config.params = {
        encryptedData: CryptoService.encrypt(JSON.stringify(config.params)),
      };

      if (config.data !== undefined && config.data !== null) {
        config.data = { encryptedData: encryptBody(config.data) };
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(new Error(error));
  },
);

globalAxiosInstance.interceptors.response.use(
  async (response) => {
    if (
      response.config.responseType === "blob" ||
      response.config.responseType === "arraybuffer"
    ) {
      return response.data;
    }

    if (
      response.config?.url &&
      isNoEncryption({
        url: response.config.url,
        method: response.config.method,
      })
    ) {
      return response.data;
    }

    if (typeof response.data === "string" && response.data.length > 0) {
      const decrypted = decryptBody(response.data);
      if (decrypted !== null && decrypted !== undefined) {
        return decrypted;
      }
    }

    return response.data;
  },

  async (error) => {
    const originalRequest = error.config;

    if (
      error.response?.status === HttpStatusCode.Unauthorized &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;

      try {
        const refreshResponse = await globalAxiosInstance.post(
          ApiRoutes.AUTH.REFRESH,
          {},
          {
            baseURL: process.env.REACT_APP_API_URL,
            withCredentials: true,
          },
        );

        const newToken = refreshResponse?.token ?? refreshResponse?.data?.token;

        if (newToken) {
          localStorage.setItem("token", newToken);
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return globalAxiosInstance(originalRequest);
        }

        localStorage.clear();
        navigateTo("/");
      } catch (refreshError) {
        console.error("Token refresh failed:", refreshError);
        localStorage.clear();
        navigateTo("/");
      }
    }

    return Promise.reject(error);
  },
);
